var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Button } from '@ui';
import { Popup } from '@progress/kendo-react-popup';
import { Modal } from '@components/v2/Modal';
import TextInput from '@components/TextInput/TextInput';
import { translate } from '@utils/i18n';
import { namespace } from '../constants';
import useCreateTemplate from '../../hooks/useCreateTemplate';
function CreateOptionsButton({ proposal }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const createAnchor = React.useRef(null);
    const [createOpen, setCreateOpen] = React.useState(false);
    const [showCreateTemplateModal, setShowCreateTemplateModal] = React.useState(false);
    const [templateName, setTemplateName] = React.useState('');
    const { createTemplate } = useCreateTemplate();
    const handleCloseCreate = () => {
        setCreateOpen(false);
    };
    const handleCreate = () => {
        setCreateOpen(true);
    };
    const handleCreateTemplate = () => __awaiter(this, void 0, void 0, function* () {
        if (!templateName.trim()) {
            return;
        }
        const res = yield createTemplate.mutateAsync({
            proposalId: proposal.id.toString(),
            templateData: { name: templateName.trim() }
        });
        if (res.success) {
            setShowCreateTemplateModal(false);
            setTemplateName('');
        }
    });
    // Create button logic based on Rails template conditions
    const isCreateDisabled = !proposal.building || proposal.restrictedFinancialControls;
    const canShowInvoicing = ((_a = proposal.tenantCapabilities) === null || _a === void 0 ? void 0 : _a.hasInvoicing) && proposal.building && !((_b = proposal.userPermissions) === null || _b === void 0 ? void 0 : _b.isSales);
    const canShowService = ((_c = proposal.tenantCapabilities) === null || _c === void 0 ? void 0 : _c.hasService) && proposal.building && !((_d = proposal.userPermissions) === null || _d === void 0 ? void 0 : _d.isSales);
    const canShowInspection = (_e = proposal.userPermissions) === null || _e === void 0 ? void 0 : _e.canCreateInspection;
    const canShowInspectionSeries = (_f = proposal.tenantCapabilities) === null || _f === void 0 ? void 0 : _f.hasInspectionSeries;
    const isCreateInvoiceDisabled = proposal.hasInvoices && !((_g = proposal.tenantCapabilities) === null || _g === void 0 ? void 0 : _g.allowMultipleInvoices);
    const getCreateButtonTitle = () => {
        if (!proposal.building) {
            return translate('disabled_create_button_title', { namespace });
        }
        if (proposal.restrictedFinancialControls) {
            return translate('forbid_action_for_inactive_building', { namespace });
        }
        return '';
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { variant: "secondary", ref: createAnchor, onClick: handleCreate, disabled: isCreateDisabled, title: getCreateButtonTitle() },
            React.createElement("i", { className: "fa-light fa-plus" }),
            " ",
            translate('create', { namespace: 'features.commons' })),
        !isCreateDisabled && (React.createElement(Popup, { anchor: createAnchor.current, show: createOpen, onClose: handleCloseCreate },
            React.createElement(ClickAwayListener, { onClickAway: handleCloseCreate },
                React.createElement("div", { id: "popup_convert", className: "qmb-popup--action-list" },
                    React.createElement("ul", { role: "presentation" },
                        canShowInvoicing && (React.createElement("li", { role: "presentation" }, isCreateInvoiceDisabled ? (React.createElement("button", { type: "button", disabled: true, title: translate('disabled_create_invoice_button_title', { namespace }) },
                            React.createElement("i", { className: "fa-light fa-file-invoice-dollar" }),
                            translate('create_invoice_button', { namespace }))) : (React.createElement("button", { type: "button", onClick: () => {
                                window.location.href = `/invoices/new?proposal_id=${proposal.id}`;
                                handleCloseCreate();
                            } },
                            React.createElement("i", { className: "fa-light fa-file-invoice-dollar" }),
                            translate('create_invoice_button', { namespace }))))),
                        canShowService && (React.createElement("li", { role: "presentation" },
                            React.createElement("button", { type: "button", onClick: () => {
                                    window.location.href = `/work_orders/new?proposal_id=${proposal.id}`;
                                    handleCloseCreate();
                                } },
                                React.createElement("i", { className: "fa-light fa-screwdriver-wrench" }),
                                translate('create_work_order_button', { namespace })))),
                        React.createElement("li", { role: "presentation" },
                            React.createElement("button", { type: "button", onClick: () => {
                                    setShowCreateTemplateModal(true);
                                    handleCloseCreate();
                                } },
                                React.createElement("i", { className: "fa-light fa-shapes" }),
                                translate('create_template_button', { namespace }))),
                        canShowInspection && (React.createElement("li", { role: "presentation" }, proposal.hasChildInspection ? (React.createElement("button", { type: "button", disabled: true, title: translate('disabled_create_inspection_button_title', { namespace }) },
                            React.createElement("i", { className: "fa-light fa-clipboard-check" }),
                            translate('create_inspection_button', { namespace }))) : (React.createElement("button", { type: "button", onClick: () => {
                                var _a, _b;
                                window.location.href = `/inspections/new?building_id=${(_b = (_a = proposal.building) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : ''}&proposal_id=${proposal.id}&skip_inspection_series=true`;
                                handleCloseCreate();
                            } },
                            React.createElement("i", { className: "fa-light fa-clipboard-check" }),
                            translate('create_inspection_button', { namespace }))))),
                        canShowInspection && canShowInspectionSeries && (React.createElement("li", { role: "presentation" }, proposal.hasInspectionSeries ? (React.createElement("button", { type: "button", disabled: true, title: translate('disabled_create_inspection_series_button_title', { namespace }) },
                            React.createElement("i", { className: "fa-light fa-rectangle-vertical-history" }),
                            translate('create_inspection_series_button', { namespace }))) : (React.createElement("button", { type: "button", onClick: () => {
                                var _a, _b;
                                window.location.href = `/buildings/${(_b = (_a = proposal.building) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : ''}/inspection_series?proposal_id=${proposal.id}&inspection_series[current_step]=new&create_series=true`;
                                handleCloseCreate();
                            } },
                            React.createElement("i", { className: "fa-light fa-rectangle-vertical-history" }),
                            translate('create_inspection_series_button', { namespace })))))))))),
        React.createElement(Modal, { isOpen: showCreateTemplateModal, width: "560px", onClose: () => setShowCreateTemplateModal(false), title: translate('create_template', { namespace }), footer: React.createElement(React.Fragment, null,
                React.createElement(Button, { onClick: () => setShowCreateTemplateModal(false), variant: "secondary" }, translate('cancel', { namespace: 'features.commons' })),
                React.createElement(Button, { onClick: () => {
                        void handleCreateTemplate();
                    }, variant: "primary", disabled: templateName.trim() === '' || createTemplate.isLoading }, translate('create', { namespace: 'features.commons' }))) },
            React.createElement(TextInput, { value: templateName, onChange: (e) => setTemplateName(e.target.value), required: true, label: translate('template_name', { namespace }) }))));
}
export default CreateOptionsButton;
